/**
 * Separate login page for the signup-without-payment / Ailaunch flow.
 * Does not modify the current Login page. Navigate here from SignupWithoutPayment "Log in".
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthService from '../services/auth.service';
import AccountVerificationAilaunch from '../modals/AccountVerificationAilaunch';
import ConfirmMfa from '../modals/ConfirmMFA';
import { LeadsquaredService } from '../services/leadsquared/leadsquared.service';
import { toast } from 'react-toastify';
import { getAilaunchRedirectUrl } from '../utils/redirect.util';

const AilaunchLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationData, setVerificationData] = useState({
    companyId: '',
    companyName: '',
    infLeadSquaredId: '',
  });
  const [confirmMfaDialogOpen, setConfirmMfaDialogOpen] = useState(false);
  const [confirmMfaData, setConfirmMfaData] = useState({
    challengeName: '',
    session: '',
  });
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({ email: '', password: '' });
    let hasErrors = false;
    const newErrors = { email: '', password: '' };
    if (!formData.email) {
      newErrors.email = 'Email is required';
      hasErrors = true;
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
      hasErrors = true;
    }
    if (hasErrors) {
      setFieldErrors(newErrors);
      return;
    }
    setIsLoading(true);
    try {
      const response = await AuthService.login({
        email: formData.email,
        password: formData.password,
      });
      if (response?.data?.ChallengeName) {
        setConfirmMfaDialogOpen(true);
        setConfirmMfaData({
          challengeName: response?.data?.ChallengeName || '',
          session: response?.data?.Session || '',
        });
      } else {
        window.location.href = getAilaunchRedirectUrl();
      }
    } catch (err: any) {
      if (err.message === 'UserNotConfirmedException') {
        setVerificationData({
          companyId: err.companyId || '',
          companyName: err.companyName || '',
          infLeadSquaredId: err.infLeadSquaredId || '',
        });
        setVerificationDialogOpen(true);
      } else {
        toast.error(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationDialogClose = () => setVerificationDialogOpen(false);

  const handleVerificationDialogProceed = async () => {
    setVerificationDialogOpen(false);
    setIsLoading(true);
    try {
      const LsData = await LeadsquaredService.getLeadsByEmail(formData.email);
      const LsId = (LsData as any)?.ProspectID;
      if (LsId) {
        await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: 'Email Verified' });
        await LeadsquaredService.updateLsField({
          leadId: LsId,
          fields: [{ Attribute: 'mx_Status', Value: 'Active' }],
        });
      }
      window.location.href = getAilaunchRedirectUrl();
    } catch {
      window.location.href = getAilaunchRedirectUrl();
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmMfaDialogClose = () => setConfirmMfaDialogOpen(false);

  const handleConfirmMfaDialogProceed = () => {
    handleConfirmMfaDialogClose();
    window.location.href = getAilaunchRedirectUrl();
  };

  return (
    <Box
      sx={{
        height: '100vh',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: verificationDialogOpen ? '#F8F0FF' : '#FDF2F4',
        p: 1.5,
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        {verificationDialogOpen ? (
          <AccountVerificationAilaunch
            inline
            onClose={handleVerificationDialogClose}
            onProceed={handleVerificationDialogProceed}
            phoneNumber=""
            email={formData.email}
            password={formData.password}
            companyId={verificationData.companyId}
            companyName={verificationData.companyName}
            infLeadSquaredId={verificationData.infLeadSquaredId}
          />
        ) : (
          <>
            <Box sx={{ textAlign: 'center', width: '100%', mb: 1.5 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  fontWeight: 700,
                  color: '#2C303E',
                  marginBottom: 0.5,
                  lineHeight: 1.2,
                }}
              >
                Log in
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: '#5B6171',
                  lineHeight: 1.3,
                }}
              >
                Log in to your account.
              </Typography>
            </Box>

            <Box
              sx={{
                maxWidth: 480,
                width: '100%',
                p: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                borderRadius: 4,
                bgcolor: '#ffffff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  minHeight: 44,
                  '& .MuiOutlinedInput-input': { py: 1.25, fontSize: '1rem' },
                  '& fieldset': { borderColor: '#D1D5DB' },
                  '&:hover fieldset': { borderColor: '#9ca3af' },
                  '&.Mui-focused': {
                    boxShadow: 'none',
                    '& fieldset': { borderColor: '#9B51E0', borderWidth: 1, boxShadow: 'none' },
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#9B51E0',
                  fontSize: '1rem',
                  fontWeight: 400,
                  '&.Mui-focused': { color: '#9B51E0' },
                },
              }}
              component="form"
              onSubmit={handleSubmit}
            >
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="email"
                disabled={isLoading}
              />
              <TextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="current-password"
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        sx={{ color: '#757575' }}
                      >
                        {showPassword ? <VisibilityOffIcon sx={{ color: 'inherit' }} /> : <VisibilityIcon sx={{ color: 'inherit' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  cursor: 'pointer',
                  display: 'block',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#9B51E0',
                }}
                onClick={() => navigate('/forgot-password-ailaunch')}
              >
                Forgot your password?
              </Typography>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: '#9B51E0',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { bgcolor: '#8B41D0' },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
              </Button>

              <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.875rem', fontWeight: 400, color: '#888C95' }}>
                Don&apos;t have an account?{' '}
                <Typography
                  component="span"
                  sx={{ color: '#9B51E0', cursor: 'pointer', fontWeight: 400, textDecoration: 'underline' }}
                  onClick={() => navigate('/signup-ai-flow')}
                >
                  Sign up
                </Typography>
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <ConfirmMfa
        open={confirmMfaDialogOpen}
        onClose={handleConfirmMfaDialogClose}
        onProceed={handleConfirmMfaDialogProceed}
        phoneNumber=""
        email={formData.email}
        challengeName={confirmMfaData.challengeName}
        session={confirmMfaData.session}
      />
    </Box>
  );
};

export default AilaunchLogin;
